import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { useAuthToken } from './useAuthToken';
import { logger } from '../utils/logger';

// Constants for localStorage keys
const AUTO_LOGOUT_ENABLED = 'autoLogoutEnabled';

// Define the settings interface
type UserSettingsContextType = {
  autoLogoutEnabled: boolean;
  setAutoLogoutEnabled: (enabled: boolean) => void;
};

// Default settings
const initialType: UserSettingsContextType = {
  autoLogoutEnabled: true, // Auto logout is enabled by default
  setAutoLogoutEnabled: (enabled: boolean) => {},
};

// Create the context
const UserSettingsContext = createContext(initialType);

export const UserSettingsProvider = ({children}: { children: React.ReactNode }) => {
  logger.info('[UserSettings] UserSettingsProvider rendering');
  const { token } = useAuthToken();
  const [autoLogoutEnabled, setAutoLogoutEnabledState] = useState<boolean>(
    localStorage.getItem(AUTO_LOGOUT_ENABLED) !== 'false' // Default to true if not explicitly set to false
  );
  
  // Set auto logout enabled state
  const setAutoLogoutEnabled = useCallback((enabled: boolean) => {
    setAutoLogoutEnabledState(enabled);
    localStorage.setItem(AUTO_LOGOUT_ENABLED, enabled ? 'true' : 'false');
    document.dispatchEvent(new Event('storageChange'));
  }, []);
  
  // Listen for storage changes
  const onLocalStateChange = useCallback(event => {
    // eslint-disable-next-line eqeqeq
    if ((!event.storageArea || event.storageArea != localStorage) && event.type !== 'storageChange') return;
    
    setTimeout(() => {
      const newAutoLogoutEnabled = localStorage.getItem(AUTO_LOGOUT_ENABLED) !== 'false';
      setAutoLogoutEnabledState(prevState => {
        if (prevState !== newAutoLogoutEnabled) {
          return newAutoLogoutEnabled;
        }
        return prevState;
      });
    }, 50);
  }, []); // Remove autoLogoutEnabled dependency to prevent constant recreation
  
  // Set up event listeners
  useEffect(() => {
    document.addEventListener('storageChange', onLocalStateChange, false);
    window.addEventListener('storage', onLocalStateChange, false);
    
    return () => {
      document.removeEventListener('storageChange', onLocalStateChange);
      window.removeEventListener('storage', onLocalStateChange);
    };
  }, [onLocalStateChange]);
  
  // Clear persisted setting on logout so the next login syncs from server
  useEffect(() => {
    if (!token) {
      localStorage.removeItem(AUTO_LOGOUT_ENABLED);
      setAutoLogoutEnabledState(true);
    }
  }, [token]);
  
  return (
    <UserSettingsContext.Provider value={{
      autoLogoutEnabled,
      setAutoLogoutEnabled,
    }}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettings = () => useContext(UserSettingsContext);

/** Sync auto-logout preference from API user record into localStorage + React state */
export const initUserSettingsFromUserData = (userData: {
  autoLogoutEnabled?: boolean | null;
}) => {
  if (userData?.autoLogoutEnabled === undefined || userData?.autoLogoutEnabled === null) {
    return;
  }

  const enabled = userData.autoLogoutEnabled;
  const storedValue = enabled ? 'true' : 'false';
  const current = localStorage.getItem(AUTO_LOGOUT_ENABLED);

  if (current !== storedValue) {
    localStorage.setItem(AUTO_LOGOUT_ENABLED, storedValue);
    document.dispatchEvent(new Event('storageChange'));
    logger.info(
      `[UserSettings] Auto logout synced from user data: ${enabled ? 'enabled' : 'disabled'}`,
    );
  }
};
